/**
 * CSR Backpay Report — pt.90 (2026-09-19).
 *
 * UI for the previously backend-only POST /api/payroll/csr-backpay-report
 * endpoint (shipped in pt.88, invoked only via curl until now). Splits a
 * custom date range into consecutive Mon-Sun weeks, evaluates the $7,500
 * CSR threshold PER week, and returns per-week + total CSR pool math.
 *
 * Access: same allowlist as Payroll — Nathanael, Missie, platform_admin.
 * Route protection lives at App.js + Sidebar; this component assumes it
 * only renders for eligible users. Belt-and-suspenders: it degrades to a
 * clear 403 error message if the endpoint rejects the caller.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarRange, Play, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const money = (v) =>
  v == null
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Stored pay_week_monday can be "20260914"; read it as 2026-09-14.
const normMonday = (v) => {
  const s = String(v || "").trim();
  return /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : s;
};
const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

export default function CsrBackpayReport() {
  const { slug } = useParams();
  const [runs, setRuns] = useState([]);
  const [picked, setPicked] = useState([]); // CC-016: one run per pay week
  const [startMonday, setStartMonday] = useState("");
  const [endDate, setEndDate] = useState("");
  const [overrideJobs, setOverrideJobs] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [real, test] = await Promise.all([
          api.get(`/payroll/runs?tenant_slug=${slug}`),
          api.get(`/payroll/test-runs?tenant_slug=${slug}`).catch(() => ({ data: [] })),
        ]);
        const asList = (d) => (Array.isArray(d) ? d : (d?.rows || d?.runs || []));
        const list = [
          ...asList(real.data).map((r) => ({ ...r, _kind: "real", _monday: normMonday(r.pay_week_monday) })),
          ...asList(test.data).filter((r) => !r.is_custom_range)
            .map((r) => ({ ...r, _kind: "test", _monday: normMonday(r.pay_week_monday) })),
        ];
        // Prefer approved runs first, then sent-for-approval, then anything
        // else — but keep them all pickable in case Brett wants to compute
        // against a specific in-progress run.
        const rank = (r) =>
          r.approved_by ? 0 : r.sent_for_approval_at ? 1 : 2;
        list.sort((a, b) => (a._monday || "").localeCompare(b._monday || "") || rank(a) - rank(b));
        setRuns(list);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [slug]);

  const isMondayIso = (iso) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
    const d = new Date(iso + "T00:00:00");
    return !isNaN(d) && d.getUTCDay() === 1;
  };

  const parseOverrideJobs = (raw) =>
    raw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const spanDays = useMemo(() => {
    if (!startMonday || !endDate) return null;
    const s = new Date(startMonday + "T00:00:00");
    const e = new Date(endDate + "T00:00:00");
    if (isNaN(s) || isNaN(e)) return null;
    return Math.round((e - s) / 86400000) + 1;
  }, [startMonday, endDate]);

  const inRange = (r) => startMonday && endDate && r._monday >= startMonday && r._monday <= endDate;
  // CC-016: auto-tick runs inside the range (approved real runs win; one per week).
  useEffect(() => {
    if (!isMondayIso(startMonday) || !endDate) return;
    const seen = new Set();
    const auto = [];
    runs.filter(inRange).forEach((r) => {
      if (!seen.has(r._monday)) { seen.add(r._monday); auto.push(r.id); }
    });
    setPicked(auto);
  }, [startMonday, endDate, runs]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const generate = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    setReport(null);
    if (!picked.length) {
      setErr("Pick one payroll run per pay week in the range.");
      return;
    }
    if (!isMondayIso(startMonday)) {
      setErr("start_monday must be a Monday in YYYY-MM-DD format.");
      return;
    }
    if (!endDate || endDate < startMonday) {
      setErr("end_date must be on or after start_monday.");
      return;
    }
    setBusy(true);
    try {
      const overrideList = parseOverrideJobs(overrideJobs);
      const body = { start_monday: startMonday, end_date: endDate, run_ids: picked };
      if (overrideList.length) body.overlays = { csr_converted_jobs: overrideList };
      const { data } = await api.post(
        `/payroll/csr-backpay-report?tenant_slug=${slug}`,
        body,
      );
      setReport(data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="csr-backpay-page">
      <header>
        <Link
          to={`/${slug}/app/payroll`}
          className="inline-flex items-center gap-1 text-[12px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420] mb-1"
          data-testid="csr-backpay-back-link"
        >
          <ArrowLeft size={12} /> Back to Payroll
        </Link>
        <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold">
          Payroll · CSR Backpay
        </div>
        <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">
          CSR backpay report
        </h1>
        <p className="text-[14px] text-[color:var(--cc-ink-muted)] mt-1 max-w-[780px]">
          Splits a custom date range into consecutive Mon-Sun weeks and evaluates the
          $7,500 CSR-follow-up threshold <b>within each week separately</b>. Use this
          for retroactive backpay when paystubs went out under a flat rule but the
          real weekly threshold should have applied.
        </p>
      </header>

      <form
        onSubmit={generate}
        className="bg-white border border-[#e7e9ee] rounded-xl p-5 max-w-[900px] space-y-4"
        data-testid="csr-backpay-form"
      >
        <div>
          <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
            Source runs — one per pay week
          </label>
          <div className="border border-[#e7e9ee] rounded-lg max-h-[260px] overflow-y-auto divide-y divide-[#eef1f6]" data-testid="csr-backpay-run-picker">
            {runs.length === 0 && <div className="px-3 py-2 text-[12.5px] text-[color:var(--cc-ink-muted)]">No runs found.</div>}
            {runs.map((r) => (
              <label key={r.id} className={`flex items-center gap-2 px-3 py-1.5 text-[12.5px] cursor-pointer ${inRange(r) ? "" : "opacity-60"}`}
                     data-testid={`csr-backpay-run-option-${r.id}`}>
                <input type="checkbox" checked={picked.includes(r.id)} onChange={() => togglePick(r.id)}
                       data-testid={`csr-backpay-run-check-${r.id}`} />
                <span className="font-mono-data">{r._monday || r.pay_week_monday}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${r._kind === "test" ? "bg-[#f3e8ff] text-[#6b21a8]" : "bg-[#e8f3fb] text-[#005a94]"}`}>
                  {r._kind === "test" ? "Test" : "Real"}
                </span>
                <span className="text-[color:var(--cc-ink-muted)]">
                  {r.approved_by ? `APPROVED by ${r.approved_by}` : r.sent_for_approval_at ? "sent for approval" : (r.status || "draft")}
                  {r.label ? ` · ${r.label}` : ""}
                </span>
              </label>
            ))}
          </div>
          <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1" data-testid="csr-backpay-picked-count">
            {picked.length} run(s) picked. Each week in the range must have exactly one run; runs in the range are ticked
            automatically (approved first). CSR-converted jobs come from each run's marks plus matched CSR sales.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
              Start Monday
            </label>
            <input
              type="date"
              data-testid="csr-backpay-start-monday"
              value={startMonday}
              onChange={(e) => setStartMonday(e.target.value)}
              className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none font-mono-data"
            />
            {startMonday && !isMondayIso(startMonday) && (
              <div className="text-[11px] text-[#7a1c1c] mt-1">Must be a Monday.</div>
            )}
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
              End date
            </label>
            <input
              type="date"
              data-testid="csr-backpay-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none font-mono-data"
            />
          </div>
          {spanDays != null && (
            <div className="col-span-2 text-[11.5px] text-[#8a5a1a] font-semibold flex items-center gap-1.5">
              <CalendarRange size={12} /> Span: {spanDays} day{spanDays === 1 ? "" : "s"}
              {" · "}
              {Math.ceil(spanDays / 7)} Mon-Sun week{Math.ceil(spanDays / 7) === 1 ? "" : "s"} will be evaluated
            </div>
          )}
        </div>

        <div>
          <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
            CSR-converted jobs override <span className="text-[color:var(--cc-ink-muted)] normal-case tracking-normal">(optional — one per line or comma-separated)</span>
          </label>
          <textarea
            data-testid="csr-backpay-override-jobs"
            rows={3}
            value={overrideJobs}
            onChange={(e) => setOverrideJobs(e.target.value)}
            placeholder="37090, 37097, 36267&#10;35542"
            className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none font-mono-data"
          />
          <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Leave blank to use the run's persisted <code className="font-mono-data">csr_converted_jobs</code>.
            Supply an explicit list here to override for this one-off backpay report only —
            does not modify the run's persisted overlays.
          </div>
        </div>

        {err && (
          <div className="bg-[#fdecec] border border-[#f7c1c1] rounded-lg px-3 py-2 text-[12.5px] text-[#7a1c1c] flex items-start gap-2" data-testid="csr-backpay-err">
            <AlertCircle size={14} strokeWidth={1.9} className="mt-0.5" />
            <div>{typeof err === "string" ? err : JSON.stringify(err)}</div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="csr-backpay-generate-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a94] disabled:opacity-60"
          >
            <Play size={14} strokeWidth={2} /> {busy ? "Generating…" : "Generate report"}
          </button>
        </div>
      </form>

      {report && (
        <div className="bg-white border border-[#e7e9ee] rounded-xl p-5 max-w-[1100px] space-y-4" data-testid="csr-backpay-results">
          <div>
            <div className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
              Aggregate — {report.start_monday} → {report.end_date} · {report.totals?.week_count || 0} week(s)
            </div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <Stat label="Total CSR pool" value={money(report.totals?.csr_pool_total_dollars)} highlight />
              <Stat label="Total CSR-converted revenue" value={money(report.totals?.csr_converted_revenue_total)} />
              <Stat
                label="Weeks at / below threshold"
                value={`${report.totals?.weeks_at_threshold || 0} / ${report.totals?.weeks_below_threshold || 0}`}
              />
            </div>
          </div>

          <div className="overflow-hidden border border-[#eef1f6] rounded-lg">
            <table className="w-full text-[12.5px]" data-testid="csr-backpay-weeks-table">
              <thead className="bg-[#f9fafb] text-[10.5px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)]">
                <tr>
                  <th className="text-left py-2 px-3">Pay week</th>
                  <th className="text-right py-2 px-3">CSR revenue</th>
                  <th className="text-right py-2 px-3">Threshold</th>
                  <th className="text-center py-2 px-3">At/Below</th>
                  <th className="text-right py-2 px-3">%</th>
                  <th className="text-right py-2 px-3">Pool $</th>
                  <th className="text-right py-2 px-3">Jobs</th>
                </tr>
              </thead>
              <tbody>
                {(report.weeks || []).map((w) => (
                  <tr key={w.pay_week_monday} className="border-t border-[#eef1f6]" data-testid={`csr-backpay-week-${w.pay_week_monday}`}>
                    <td className="py-2 px-3 font-mono-data">
                      {w.pay_week_monday} → {w.pay_week_sunday}
                      <div className="text-[10.5px] text-[color:var(--cc-ink-muted)]">{w.run_kind === "test" ? "Test" : "Real"} run {String(w.run_id || "").slice(0, 8)}</div>
                      {(w.warnings || []).map((wn) => (
                        <div key={wn.code} className="text-[10.5px] text-[#8a5a1a] font-semibold" data-testid={`csr-backpay-warning-${w.pay_week_monday}`}>
                          {wn.message}
                        </div>
                      ))}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data">{money(w.csr_converted_revenue_this_week)}</td>
                    <td className="py-2 px-3 text-right font-mono-data">{money(w.csr_threshold)}</td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${
                          w.threshold_hit ? "bg-[#e0f4ec] text-[#0e6b52]" : "bg-[#fef3d9] text-[#8a5a1a]"
                        }`}
                      >
                        {w.threshold_hit ? "At" : "Below"}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data">{pct(w.csr_pct_used)}</td>
                    <td className="py-2 px-3 text-right font-mono-data font-bold">{money(w.csr_pool_dollars)}</td>
                    <td className="py-2 px-3 text-right font-mono-data text-[color:var(--cc-ink-muted)]">{w.jobs_in_week}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {report.weeks?.some((w) => (w.per_csr_breakdown || []).length > 0) && (
            <div>
              <div className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-2">
                Per-CSR share breakdown
              </div>
              <div className="space-y-3">
                {report.weeks.map((w) => (
                  (w.per_csr_breakdown || []).length > 0 && (
                    <div key={w.pay_week_monday} className="border border-[#eef1f6] rounded-lg overflow-hidden">
                      <div className="bg-[#f9fafb] px-3 py-1.5 text-[11.5px] font-mono-data font-semibold flex items-center justify-between">
                        <span>{w.pay_week_monday} → {w.pay_week_sunday} · pool {money(w.csr_pool_dollars)}</span>
                        <ManualOverrideButton week={w} slug={slug} onAdded={generate} />
                      </div>
                      <table className="w-full text-[12px]">
                        <thead className="text-[10.5px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)]">
                          <tr>
                            <th className="text-left py-1.5 px-3">CSR</th>
                            <th className="text-right py-1.5 px-3">Share %</th>
                            <th className="text-right py-1.5 px-3">Share $</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(w.per_csr_breakdown || []).map((b) => (
                            <tr key={b.csr_profile_id || b.name} className="border-t border-[#eef1f6]">
                              <td className="py-1.5 px-3">{b.name}</td>
                              <td className="py-1.5 px-3 text-right font-mono-data">{pct(b.share_pct)}</td>
                              <td className="py-1.5 px-3 text-right font-mono-data font-semibold">{money(b.share_dollars)}</td>
                            </tr>
                          ))}
                          {(w.manual_override_lines || []).map((ml) => (
                            <tr key={ml.override_id} className="border-t border-[#f0d68b] bg-[#fef8e5]" data-testid={`manual-override-line-${ml.job_number}`}>
                              <td className="py-1.5 px-3" colSpan={3}>
                                <div className="text-[11px] font-bold uppercase tracking-wide text-[#8a5a1a] mb-0.5">
                                  Manual override · job #{ml.job_number} · {money(ml.manual_invoice_total)} @ {pct(ml.csr_pct_used)}
                                </div>
                                <div className="text-[11.5px]">
                                  {(ml.per_csr_breakdown || []).map((b, i) => (
                                    <span key={i} className="font-mono-data mr-4">
                                      {b.name} · {money(b.share_dollars)}
                                    </span>
                                  ))}
                                </div>
                                <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] mt-0.5 italic">
                                  {ml.reason} · entered by {ml.entered_by}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ))}
              </div>
              {report.totals?.manual_override_count > 0 && (
                <div className="mt-3 text-[11.5px] text-[#8a5a1a]" data-testid="manual-override-summary">
                  <b>{report.totals.manual_override_count}</b> manual override{report.totals.manual_override_count === 1 ? "" : "s"} — {money(report.totals.manual_override_dollars)} outside pool math
                </div>
              )}
            </div>
          )}

          <details className="text-[11px]">
            <summary className="cursor-pointer text-[color:var(--cc-ink-muted)] font-semibold uppercase tracking-widest">
              Raw JSON
            </summary>
            <pre className="mt-2 p-3 bg-[#f9fafb] border border-[#eef1f6] rounded overflow-auto max-h-[400px] font-mono-data text-[10.5px]">
              {JSON.stringify(report, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? "bg-[#e7f4fb] border-[#c2dff0]" : "bg-[#f9fafb] border-[#eef1f6]"}`}>
      <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
        {label}
      </div>
      <div className={`font-mono-data font-bold mt-1 ${highlight ? "text-[#005a94] text-[22px]" : "text-[#0e1420] text-[18px]"}`}>
        {value}
      </div>
    </div>
  );
}


/* pt.92 — inline manual override entry per week bucket. Persists via
   POST /api/csr-backpay/manual-override; rejects with 409 if job_number
   resolves to a computed payroll_run_payouts row (real data always
   wins). On success the parent regenerates the report so the manual
   line appears in the correct week's Per-CSR breakdown. */
function ManualOverrideButton({ week, slug, onAdded }) {
  const [open, setOpen] = useState(false);
  const [jobNumber, setJobNumber] = useState("");
  const [invoiceTotal, setInvoiceTotal] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post(`/csr-backpay/manual-override?tenant_slug=${slug}`, {
        job_number: jobNumber.trim(),
        week_monday: week.pay_week_monday,
        manual_invoice_total: parseFloat(invoiceTotal),
        reason: reason.trim(),
      });
      setOpen(false); setJobNumber(""); setInvoiceTotal(""); setReason("");
      onAdded?.();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || JSON.stringify(d) || ex.message));
    } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={`add-manual-override-${week.pay_week_monday}`}
        className="text-[10.5px] font-semibold text-[#8a5a1a] hover:underline">
        + Add manual entry
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="flex items-center gap-1.5" data-testid={`manual-override-form-${week.pay_week_monday}`}>
      <input type="text" value={jobNumber} onChange={(e) => setJobNumber(e.target.value.replace(/[^\d]/g, ""))}
        placeholder="Job #" required className="w-[80px] px-1.5 py-0.5 text-[11px] border border-[#e7e9ee] rounded font-mono-data"
        data-testid="manual-override-job-input" />
      <input type="number" step="0.01" min="0.01" value={invoiceTotal} onChange={(e) => setInvoiceTotal(e.target.value)}
        placeholder="Invoice $" required className="w-[100px] px-1.5 py-0.5 text-[11px] border border-[#e7e9ee] rounded font-mono-data"
        data-testid="manual-override-total-input" />
      <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (required)" required className="w-[180px] px-1.5 py-0.5 text-[11px] border border-[#e7e9ee] rounded"
        data-testid="manual-override-reason-input" />
      <button type="submit" disabled={busy} data-testid="manual-override-submit"
        className="px-2 py-0.5 text-[11px] font-semibold rounded bg-[#8a5a1a] text-white disabled:opacity-60">
        {busy ? "…" : "Save"}
      </button>
      <button type="button" onClick={() => { setOpen(false); setErr(null); }} data-testid="manual-override-cancel"
        className="text-[10.5px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]">cancel</button>
      {err && <span className="text-[10px] text-[#7a1c1c] ml-1" data-testid="manual-override-err">{err}</span>}
    </form>
  );
}
